import React, { FC, useCallback, useEffect, useRef, useState } from "react";
import { Box, Button, Heading, Input, Label, Text } from "@telia/teddy";
import {
    API_URL_DEALER_LOGIN_VALIDATE_CREDENTIALS,
    API_URL_DEALER_LOGIN_CHECK_SESSION,
    API_URL_DEALER_LOGIN_SEND_PIN,
    API_URL_DEALER_LOGIN_VERIFY_PIN,
} from "../../api";
import "./DealerCodeLoginModal.less";

type Step = "CREDENTIALS" | "MOBILE_INPUT" | "PIN_INPUT";

const MAX_PIN_ATTEMPTS = 5;
const OTP_LENGTH = 4;
const formatPhoneNumberForDisplay = (phoneNumber: string): string => {
    const digits = phoneNumber.replace(/\D/g, "");
    const localNumber = digits.startsWith("47") && digits.length === 10 ? digits.slice(2) : digits;

    if (!/^\d{8}$/.test(localNumber)) {
        return phoneNumber.trim();
    }
    return localNumber.replace(/(\d{3})(\d{2})(\d{3})/, "$1 $2 $3");
};

const postJson = async (url: string, body: Record<string, string | undefined>): Promise<Response> =>
    fetch(url, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "same-origin",
        body: JSON.stringify(body),
    });

interface Props {
    open: boolean;
    onClose: () => void;
}

const DealerCodeLoginModal: FC<Props> = ({ open, onClose }) => {
    const dialogRef = useRef<HTMLDialogElement>(null);
    const verifyPinFormRef = useRef<HTMLFormElement>(null);
    const lastAutoSubmittedPinRef = useRef("");

    useEffect(() => {
        const dialog = dialogRef.current;
        if (!dialog) return;
        if (open && !dialog.open) {
            dialog.showModal();
        } else if (!open && dialog.open) {
            dialog.close();
        }
    }, [open]);

    useEffect(() => {
        const dialog = dialogRef.current;
        if (!dialog) return;
        const handleClose = () => onClose();
        dialog.addEventListener("close", handleClose);
        return () => dialog.removeEventListener("close", handleClose);
    }, [onClose]);
    const [step, setStep] = useState<Step>("CREDENTIALS");
    const [dealerCode, setDealerCode] = useState("");
    const [password, setPassword] = useState("");
    const [phoneNumber, setPhoneNumber] = useState("");
    const [pinDigits, setPinDigits] = useState(Array(OTP_LENGTH).fill(""));
    const [error, setError] = useState<string | null>(null);
    const [attemptsLeft, setAttemptsLeft] = useState(MAX_PIN_ATTEMPTS);
    const [attemptsExhausted, setAttemptsExhausted] = useState(false);
    const [isLoading, setIsLoading] = useState(false);

    const resetFormState = useCallback(() => {
        setStep("CREDENTIALS");
        setDealerCode("");
        setPassword("");
        setPhoneNumber("");
        setPinDigits(Array(OTP_LENGTH).fill(""));
        lastAutoSubmittedPinRef.current = "";
        setError(null);
        setAttemptsLeft(MAX_PIN_ATTEMPTS);
        setAttemptsExhausted(false);
        setIsLoading(false);
    }, []);

    useEffect(() => {
        resetFormState();
    }, [open, resetFormState]);

    // If DEALER_LOGIN_SESSION is still valid, skip dealer credentials step
    useEffect(() => {
        if (!open) return;
        fetch(API_URL_DEALER_LOGIN_CHECK_SESSION, { credentials: "same-origin" })
            .then((res) => res.json())
            .then((data) => {
                if (data.valid) {
                    setStep("MOBILE_INPUT");
                }
            })
            .catch(() => {
                // Session check failed — stay on CREDENTIALS step
            });
    }, [open]);

    useEffect(() => {
        if (step !== "MOBILE_INPUT") return;

        const rafId = window.requestAnimationFrame(() => {
            const phoneInput = document.getElementById("phone-number") as HTMLInputElement | null;
            phoneInput?.focus();
        });

        return () => window.cancelAnimationFrame(rafId);
    }, [step]);

    useEffect(() => {
        if (step !== "PIN_INPUT") return;

        const rafId = window.requestAnimationFrame(() => {
            const pinInput = document.getElementById("otp-pin-0") as HTMLInputElement | null;
            pinInput?.focus();
        });

        return () => window.cancelAnimationFrame(rafId);
    }, [step]);

    useEffect(() => {
        if (step !== "PIN_INPUT" || isLoading || attemptsExhausted) return;

        const pin = pinDigits.join("");
        if (pin.length !== OTP_LENGTH || pin === lastAutoSubmittedPinRef.current) return;

        lastAutoSubmittedPinRef.current = pin;
        verifyPinFormRef.current?.requestSubmit();
    }, [attemptsExhausted, isLoading, pinDigits, step]);

    const handleValidateCredentials = async (e: React.FormEvent) => {
        e.preventDefault();
        setError(null);
        setIsLoading(true);

        try {
            const response = await postJson(API_URL_DEALER_LOGIN_VALIDATE_CREDENTIALS, {
                dealerCode: dealerCode.trim(),
                password,
            });

            const data = await response.json();
            if (!response.ok) {
                setError(data.error ?? "Ukjent feil. Prøv igjen.");
                return;
            }

            setStep("MOBILE_INPUT");
        } catch {
            setError("Nettverksfeil. Sjekk tilkoblingen og prøv igjen.");
        } finally {
            setIsLoading(false);
        }
    };

    const sendPin = async (phone?: string) => {
        setError(null);
        setIsLoading(true);
        setAttemptsExhausted(false);
        setAttemptsLeft(MAX_PIN_ATTEMPTS);
        setPinDigits(Array(OTP_LENGTH).fill(""));
        lastAutoSubmittedPinRef.current = "";

        try {
            const body = phone ? { phoneNumber: phone } : {};
            const response = await postJson(API_URL_DEALER_LOGIN_SEND_PIN, body);

            const data = await response.json();
            if (!response.ok) {
                setError(data.error ?? "Ukjent feil. Prøv igjen.");
                return;
            }

            setStep("PIN_INPUT");
        } catch {
            setError("Nettverksfeil. Sjekk tilkoblingen og prøv igjen.");
        } finally {
            setIsLoading(false);
        }
    };

    const handlePhoneNumberChange = (value: string) => {
        setPhoneNumber(value);

        if (isLoading) return;

        const digits = value.replace(/\D/g, "");
        if (digits.length === 8) {
            void sendPin(value.trim());
        }
    };

    const resendPin = async () => {
        setError(null);
        setIsLoading(true);
        setAttemptsExhausted(false);
        setAttemptsLeft(MAX_PIN_ATTEMPTS);
        setPinDigits(Array(OTP_LENGTH).fill(""));
        lastAutoSubmittedPinRef.current = "";

        try {
            const response = await postJson(API_URL_DEALER_LOGIN_SEND_PIN, {});

            const data = await response.json();
            if (!response.ok) {
                setError(data.error ?? "Ukjent feil. Prøv igjen.");
            }
        } catch {
            setError("Nettverksfeil. Sjekk tilkoblingen og prøv igjen.");
        } finally {
            setIsLoading(false);
        }
    };

    const handleVerifyPin = async (e: React.FormEvent) => {
        e.preventDefault();
        setError(null);

        const pin = pinDigits.join("").trim();
        if (pin.length !== OTP_LENGTH) {
            setError("Tast inn 4-sifret engangskode.");
            return;
        }

        setIsLoading(true);

        try {
            const response = await postJson(API_URL_DEALER_LOGIN_VERIFY_PIN, {
                pin,
            });

            const data = await response.json();

            if (!response.ok) {
                if (data.attemptsExhausted) {
                    setAttemptsExhausted(true);
                    setAttemptsLeft(0);
                } else if (typeof data.attemptsLeft === "number") {
                    setAttemptsLeft(data.attemptsLeft);
                }
                setError(data.error ?? "Feil kode. Prøv igjen.");
                return;
            }

            if (data.redirectUrl) {
                window.location.assign(data.redirectUrl);
            }
        } catch {
            setError("Nettverksfeil. Sjekk tilkoblingen og prøv igjen.");
        } finally {
            setIsLoading(false);
        }
    };

    const handlePinDigitChange = (index: number, value: string) => {
        const nextDigit = value.replace(/\D/g, "").slice(-1);
        lastAutoSubmittedPinRef.current = "";

        setPinDigits((prev) => {
            const next = [...prev];
            next[index] = nextDigit;
            return next;
        });

        if (nextDigit && index < OTP_LENGTH - 1) {
            const nextInput = document.getElementById(`otp-pin-${index + 1}`) as HTMLInputElement | null;
            nextInput?.focus();
        }
    };

    const handlePinDigitKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === "Backspace" && !pinDigits[index] && index > 0) {
            const prevInput = document.getElementById(`otp-pin-${index - 1}`) as HTMLInputElement | null;
            prevInput?.focus();
        }
    };

    const handlePinPaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
        e.preventDefault();
        lastAutoSubmittedPinRef.current = "";
        const pastedDigits = e.clipboardData.getData("text").replace(/\D/g, "").slice(0, OTP_LENGTH);
        if (!pastedDigits) return;

        const next = Array(OTP_LENGTH).fill("") as string[];
        for (let i = 0; i < pastedDigits.length; i += 1) {
            next[i] = pastedDigits[i];
        }
        setPinDigits(next);

        const focusIndex = Math.min(pastedDigits.length, OTP_LENGTH) - 1;
        const input = document.getElementById(`otp-pin-${focusIndex}`) as HTMLInputElement | null;
        input?.focus();
    };

    return (
        <dialog ref={dialogRef} className="dealer-code-login-modal__dialog">
            <Box className="dealer-code-login-modal">
                {step === "CREDENTIALS" && (
                    <form onSubmit={handleValidateCredentials} className="dealer-code-login-modal__step">
                        <Heading mb="100" as="h2" variant="title-200">
                            Logg inn
                        </Heading>

                        <Box>
                            <Label htmlFor="dealer-code" className="dealer-code-login-modal__input-label">
                                Forhandlerkode
                            </Label>
                            <Input
                                id="dealer-code"
                                type="text"
                                value={dealerCode}
                                onChange={(e) => setDealerCode(e.target.value)}
                                autoComplete="username"
                                required
                                data-di-mask
                            />
                        </Box>

                        <Box>
                            <Label htmlFor="dealer-password" className="dealer-code-login-modal__input-label">
                                Passord
                            </Label>
                            <Input
                                id="dealer-password"
                                type="password"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                autoComplete="current-password"
                                required
                                data-di-mask
                            />
                        </Box>

                        {error && <Text className="dealer-code-login-modal__error">{error}</Text>}

                        <Box className="dealer-code-login-modal__actions">
                            <Button type="submit" variant="primary" loading={isLoading} disabled={isLoading}>
                                Neste
                            </Button>
                            <Button type="button" variant="secondary" onClick={onClose} disabled={isLoading}>
                                Avbryt
                            </Button>
                        </Box>
                    </form>
                )}

                {step === "MOBILE_INPUT" && (
                    <form
                        onSubmit={(e) => {
                            e.preventDefault();
                            sendPin(phoneNumber.trim());
                        }}
                        className="dealer-code-login-modal__step"
                    >
                        <Heading mb="100" as="h2" variant="title-200">
                            Bekreft mobilnummer
                        </Heading>

                        <Text className="dealer-code-login-modal__description">
                            Oppgi mobilnummeret som er registrert på din brukerkonto for forhandlerkode{" "}
                            <strong>{dealerCode.trim().toUpperCase()}</strong>. En engangskode sendes på SMS.
                        </Text>

                        <Box>
                            <Label htmlFor="phone-number" className="dealer-code-login-modal__input-label">
                                Mobilnummer
                            </Label>
                            <Input
                                id="phone-number"
                                type="tel"
                                value={phoneNumber}
                                onChange={(e) => handlePhoneNumberChange(e.target.value)}
                                placeholder="8 sifre"
                                autoComplete="tel"
                                required
                                data-di-mask
                            />
                        </Box>

                        {error && <Text className="dealer-code-login-modal__error">{error}</Text>}

                        <Box className="dealer-code-login-modal__actions">
                            <Button type="submit" variant="primary" loading={isLoading} disabled={isLoading}>
                                Send engangskode
                            </Button>
                            <Button
                                type="button"
                                variant="secondary"
                                onClick={() => {
                                    setStep("CREDENTIALS");
                                    setError(null);
                                }}
                                disabled={isLoading}
                            >
                                Tilbake
                            </Button>
                        </Box>
                    </form>
                )}

                {step === "PIN_INPUT" && (
                    <form ref={verifyPinFormRef} onSubmit={handleVerifyPin} className="dealer-code-login-modal__step">
                        <Heading mb="100" as="h2" variant="title-200">
                            Tast inn engangskode
                        </Heading>

                        <Text className="dealer-code-login-modal__description">
                            En engangskode er sendt til{" "}
                            <strong data-di-mask>{formatPhoneNumberForDisplay(phoneNumber)}</strong>
                        </Text>
                        <Box>
                            <Label htmlFor="otp-pin-0" className="dealer-code-login-modal__input-label">
                                Engangskode
                            </Label>
                            <Box className="dealer-code-login-modal__otp-inputs">
                                {pinDigits.map((digit, index) => (
                                    <Input
                                        key={`otp-pin-${index}`}
                                        id={`otp-pin-${index}`}
                                        className="dealer-code-login-modal__otp-input"
                                        type="text"
                                        inputMode="numeric"
                                        pattern="[0-9]*"
                                        value={digit}
                                        onChange={(e) => handlePinDigitChange(index, e.target.value)}
                                        onKeyDown={(e) => handlePinDigitKeyDown(index, e)}
                                        onPaste={handlePinPaste}
                                        autoComplete={index === 0 ? "one-time-code" : "off"}
                                        maxLength={1}
                                        required
                                        disabled={attemptsExhausted}
                                        aria-label={`Engangskode siffer ${index + 1}`}
                                    />
                                ))}
                            </Box>
                            <Text className="dealer-code-login-modal__resend-text">
                                Fikk du ikke koden?
                                <Button
                                    type="button"
                                    variant="text"
                                    className="dealer-code-login-modal__resend-link"
                                    onClick={resendPin}
                                    loading={isLoading}
                                    disabled={isLoading}
                                >
                                    Send på nytt
                                </Button>
                            </Text>
                        </Box>

                        {error && <Text className="dealer-code-login-modal__error">{error}</Text>}

                        {!attemptsExhausted && attemptsLeft < MAX_PIN_ATTEMPTS && attemptsLeft > 0 && (
                            <Text className="dealer-code-login-modal__attempts-warning">
                                {attemptsLeft} forsøk igjen.
                            </Text>
                        )}

                        <Box className="dealer-code-login-modal__actions">
                            {!attemptsExhausted && (
                                <Button type="submit" variant="primary" loading={isLoading} disabled={isLoading}>
                                    Bekreft
                                </Button>
                            )}

                            <Button
                                type="button"
                                variant="secondary"
                                className="dealer-code-login-modal__back-button"
                                onClick={() => {
                                    setStep("MOBILE_INPUT");
                                    setError(null);
                                    setAttemptsExhausted(false);
                                    setAttemptsLeft(MAX_PIN_ATTEMPTS);
                                    setPinDigits(Array(OTP_LENGTH).fill(""));
                                    lastAutoSubmittedPinRef.current = "";
                                }}
                                disabled={isLoading}
                            >
                                Tilbake
                            </Button>
                        </Box>
                    </form>
                )}
            </Box>
        </dialog>
    );
};

export default DealerCodeLoginModal;
